import { Card, Clickable } from '@warp-ds/react';
import { useEffect, useRef } from 'react';

import { useConfig } from 'shared/config/ConfigContext';
import { trackSearchClick } from 'shared/search/common/tracking';
import { formatReadableMonthYear, parseISO } from 'shared/util/datetime-fns';
import { thousandSeparated } from 'shared/util/formatting';
import { isMobile } from 'shared/util/mobile';
import { queryString } from 'shared/util/query';

import styles from './CheapestDestinations.module.css';

export type LowfareDestination = {
    origin: string;
    originCity: string;
    originCountry: string;
    destination: string;
    destinationCity: string;
    destinationCountry: string;
    departureDate: string;
    returnDate: string;
    price: number;
};

type CheapestDestinationsProps = {
    destinations: LowfareDestination[] | null;
};

const colors = [
    's-bg-negative-subtle-active',
    's-bg-primary-subtle-active',
    's-bg-warning-subtle-active',
    's-bg-info-subtle-active',
    's-bg-positive-subtle-active',
    's-bg-primary-subtle-hover',
];

const createFlightUrl = (item: LowfareDestination, flightResultUrl: string): string =>
    flightResultUrl +
    queryString({
        tripType: 'roundtrip',
        requestedOrigin: item.origin,
        requestedDestination: item.destination,
        requestedDepartureDate: item.departureDate,
        requestedReturnDate: item.returnDate,
        adults: 1,
        cabinType: 'economy',
    });

const isElementInViewport = (el: Element): boolean => {
    const rect = el.getBoundingClientRect();
    return rect.right > 0;
};

export const CheapestDestinations = ({ destinations }: CheapestDestinationsProps) => {
    const { flightResultUrl } = useConfig();
    const autoScroll = useRef(true);
    const container = useRef<HTMLDivElement>();
    const onMobile = isMobile();

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const prefersReducedMotion = mediaQuery.matches;

        // No scrolling on small screens, so no need to set up the interval
        if (onMobile || prefersReducedMotion) return;

        const elementGapWidth = 16; // must match flex gap in CSS file
        const containerScrollWidth = container.current?.scrollWidth;

        const interval = setInterval(() => {
            // Disable auto scrolling on hover or focus. If the screen suddenly has become small (i.e. resized window), also turn it off.
            if (!autoScroll.current || onMobile) return;

            // Somehow container.current can sometimes be something other than Element
            if (!container.current || typeof container.current.scrollTo !== 'function') return;

            const first = container.current.firstElementChild;

            if (first && !isElementInViewport(first)) {
                // Takes first element and appends it to the end when outside viewport
                container.current.appendChild(first);
                container.current.scrollTo(container.current.scrollLeft - first.clientWidth - elementGapWidth, 0);
            }

            if (container.current.scrollLeft !== containerScrollWidth) {
                container.current.scrollTo(container.current.scrollLeft + 1, 0);
            }
        }, 25);

        return () => clearInterval(interval);
    }, [onMobile]);

    const handleClick = (data: LowfareDestination) => {
        const item = {
            id: 'finntravel',
            type: 'Travel',
            // Due to legacy definitions in Pulse, `destination` is used instead of `arrivalCountry`
            destination: data.destinationCountry,
            arrivalCity: data.destinationCity,
            arrivalAirport: data.destination,
            departureCountry: data.originCountry,
            departureCity: data.originCity,
            departureAirport: data.origin,
            travelType: 'flight',
            tripType: 'roundtrip',
            departureDate: parseISO(data.departureDate)?.toISOString(),
            returnDate: parseISO(data.returnDate)?.toISOString(),
            numberOfAdults: 1,
            numberOfChildren: 0,
            cabin: 'economy',
        };

        // Pulse tracker id: TR_ev01
        trackSearchClick('Search flight', '/reise', 'flight', 'cheapestdestinations', item);
    };

    return (
        <section className="my-32 mx-16 md:mx-0">
            <h2>Rimelige reiser akkurat nå</h2>
            {/* biome-ignore lint/a11y/noStaticElementInteractions: Element needs to be "interactive"  */}
            <div
                className={styles.horizontalScrollingItems}
                // @ts-ignore
                ref={container}
                onMouseOver={() => {
                    autoScroll.current = false;
                }}
                onMouseOut={() => {
                    autoScroll.current = true;
                }}
                onFocus={() => {
                    autoScroll.current = false;
                }}
                onBlur={() => {
                    autoScroll.current = true;
                }}>
                {destinations?.map((data, idx) => (
                    <article key={data.origin + data.destination}>
                        <Card className={styles.horizontalScrollingItem}>
                            <header className={`${colors[idx % colors.length]} whitespace-normal p-24`}>
                                <h3 className="text-16 font-normal" id={`cheapestDestinationCard${idx}`}>
                                    <Clickable
                                        href={createFlightUrl(data, flightResultUrl)}
                                        onClick={() => handleClick(data)}
                                        title={`${data.originCity} til ${data.destinationCity}`}
                                        className="flex flex-col text-current hover:no-underline focus:no-underline">
                                        <span>{data.originCity} - </span>
                                        <span className="text-22 font-bold pt-8">{data.destinationCity}</span>
                                    </Clickable>
                                </h3>
                                <div className="mt-32 capitalize">{formatReadableMonthYear(parseISO(data.departureDate))}</div>
                            </header>
                            <footer className="px-24 py-16">
                                <p className="flex flex-col">
                                    <span>fra</span>
                                    <span className="text-22 font-bold py-2">{thousandSeparated(data.price)} kr</span>
                                    <span className="s-text-static text-14">per person</span>
                                </p>
                            </footer>
                        </Card>
                    </article>
                ))}
            </div>
        </section>
    );
};
